import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { getSalaryDetails, clearLoans, getTemplate } from "../../api/salary";
import SalaryCard from "./SalaryCard";
import { useRecoilState, useSetRecoilState, useRecoilValue } from "recoil";
import {
  employeeSearchBarState,
  toastShowState,
  salaryModalTitleState,
  toastBgState,
  toastMessageState,
  toastAutoHideState,
  isBulkSalaryUpdatedState,
  totalPresentDaysState,
  holidayState,
  holidayDatesState,
} from "../../utils/atoms/atoms";
import {
  getHoursPastMonth,
  getHoursPastMonthSunday,
  getAttdPastMonthSheet,
} from "../../api/attendance";
import { useEffect, useMemo } from "react";
import ToastNotification from "../common/ToastNotification";
import CustomModal from "../CustomModal";
import BulkUpdateModal from "./BulkUpdateModal";
import SalaryMonthCardModal from "./SalaryMonthCardModal";
import TerminatedEmployeesModal from "./TerminatedEmployeesModal";
import { HasRole } from "../../context/HasRole";
import WrongPunchesModal from "./WrongPunchesModal";
import { parseHolidayDays } from "../../utils/funcs/holidays";
import { getPastMonthYear } from "../../utils/funcs/days";
import { getDaysInMonth } from "../../utils/funcs/time";

const Salary = () => {
  const queryClient = useQueryClient();
  // toast state changes and functions
  const setToastShow = useSetRecoilState(toastShowState);
  const [toastBg, setToastBg] = useRecoilState(toastBgState);
  const [toastMessage, setToastMessage] = useRecoilState(toastMessageState);
  const [toastAutoHide, setAutoHide] = useRecoilState(toastAutoHideState);

  const [totalPresentDays, setTotalPresentDays] = useRecoilState(
    totalPresentDaysState
  );

  const clearLoanToast = () => {
    setToastShow(true);
    setToastBg("success");
    setAutoHide(true);
    setToastMessage("Loans have been cleared");
  };

  const attendanceToast = () => {
    setToastShow(true);
    setAutoHide(false);
    setToastBg("warning");
    setToastMessage("Please wait till download");
  };

  // bulk update modal
  const setSalaryModalTitle = useSetRecoilState(salaryModalTitleState);
  const salaryModalTitle = useRecoilValue(salaryModalTitleState);
  const [isSalUpdated, setIsSalUpdated] = useRecoilState(
    isBulkSalaryUpdatedState
  );

  const {
    isLoading,
    isSuccess,
    data: salary_details,
  } = useQuery({
    queryKey: ["salary"],
    queryFn: getSalaryDetails,
  });
  const {
    isLoading: isLoadingSunday,
    isSuccess: isSuccessSunday,
    data: past_month_sundays,
  } = useQuery({
    queryKey: ["past_month_sundays"],
    queryFn: getHoursPastMonthSunday,
  });
  const {
    isLoading: isLoadingHours,
    isSuccess: isSuccessHours,
    data: hours_past_month,
  } = useQuery({
    queryKey: ["hours_past_month"],
    queryFn: getHoursPastMonth,
  });

  const getSundayCount = (emp_id) => {
    const sundays = past_month_sundays?.filter((attd) => {
      return parseInt(attd.emp_code) === emp_id;
    });
    return sundays.length;
  };

  // mutation for clear loan
  const updateEmpLoan = useMutation({
    mutationFn: async () => {
      return clearLoans();
    },
    onSuccess() {
      queryClient.removeQueries({ queryKey: ["salary"] });
    },
  });

  const updateClearLoans = () => {
    if (confirm("Are you sure you want to proceed?")) {
      updateEmpLoan.mutate();
    }
  };

  const getTotalPresentDays = () => {
    let presentCount = 0;
    salary_details?.map((emp) => {
      const empDetails = hours_past_month[emp.emp_id];
      presentCount += empDetails?.present ?? 0;
    });
    setTotalPresentDays(presentCount);
  };

  const [holiday, setHoliday] = useRecoilState(holidayState);
  const [holidayDates, setHolidayDates] = useRecoilState(holidayDatesState);
  const { month: salaryMonth, year: salaryYear } = getPastMonthYear();
  const holidayDays = useMemo(
    () =>
      parseHolidayDays(holidayDates, getDaysInMonth(salaryMonth, salaryYear)),
    [holidayDates, salaryMonth, salaryYear]
  );

  useEffect(() => {
    localStorage.setItem("holiday", holiday.toString());
  }, [holiday]);

  useEffect(() => {
    setHoliday(holidayDays.length);
    localStorage.setItem("holidayDates", holidayDates);
  }, [holidayDates, holidayDays.length, setHoliday]);

  const wrongPunchEmployees = useMemo(
    () =>
      (salary_details ?? [])
        .map((employee) => ({
          emp_id: employee.emp_id,
          emp_name: employee.emp_name,
          wrongPunches: Number(
            hours_past_month?.[employee.emp_id]?.one_punch ?? 0
          ),
        }))
        .filter((employee) => employee.wrongPunches > 0)
        .sort(
          (first, second) =>
            second.wrongPunches - first.wrongPunches ||
            first.emp_name.localeCompare(second.emp_name)
        ),
    [salary_details, hours_past_month]
  );

  const [employeeSearchBar, setEmployeeSearchBar] = useRecoilState(
    employeeSearchBarState
  );

  const {
    fetchStatus: fetchStatusPastMonth,
    refetch: refetchPastMonth, // Destructure the refetch function
  } = useQuery({
    queryKey: ["sheet_past_month", holiday],
    queryFn: () => getAttdPastMonthSheet(holiday),
    enabled: false,
  });

  // whenever is fethc status changes , we close the tosat
  useEffect(() => {
    if (fetchStatusPastMonth === "idle") {
      setToastShow(false);
    }
  }, [fetchStatusPastMonth]);

  useEffect(() => {
    if (isSalUpdated) {
      queryClient.removeQueries({ queryKey: ["salary"] });
      setIsSalUpdated(false);
    }
  }, [isSalUpdated]);
  return (
    <div className="h-100">
      <div className="f-between">
        <input
          type="text"
          className="search-attd m-2 p-2"
          placeholder="Search employees"
          value={employeeSearchBar}
          onChange={(e) => setEmployeeSearchBar(String(e.target.value))}
        />

        <ToastNotification
          bg={toastBg}
          message={toastMessage}
          autohide={toastAutoHide}
        />

        <div className="f-center">
          <div className=" f-center me-2 mobile-d-none">
            <div
              className="styled-btn--dark me-2 mobile-d-none"
              data-bs-toggle="modal"
              data-bs-target="#terminatedEmployeeModal"
              onClick={() => {
                setSalaryModalTitle("Terminated Employees");
                queryClient.invalidateQueries(["terminated_employees"]);
              }}
            >
              Terminated
            </div>
            <div className="holiday-input-group me-2">
              <label htmlFor="holiday-dates">Holiday dates</label>
              <input
                id="holiday-dates"
                type="text"
                inputMode="numeric"
                value={holidayDates}
                placeholder="e.g. 2, 14, 25"
                aria-describedby="holiday-dates-count"
                onChange={(event) => setHolidayDates(event.target.value)}
              />
              <span id="holiday-dates-count">
                {holiday} {holiday === 1 ? "holiday" : "holidays"}
              </span>
            </div>
          </div>

          <div
            className="styled-btn--std me-2 mobile-d-none"
            data-bs-toggle="modal"
            data-bs-target="#wrongPunchesModal"
          >
            Wrong Punches
            {wrongPunchEmployees.length > 0 && (
              <span className="wrong-punch-badge">
                {wrongPunchEmployees.length}
              </span>
            )}
          </div>

          <div
            className="styled-btn--std me-2 mobile-d-none"
            onClick={() => {
              getTotalPresentDays();
            }}
          >
            Total Present: {totalPresentDays === 0 ? "..." : totalPresentDays}
          </div>
          <div
            className="styled-btn--std me-2 mobile-d-none"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            onClick={() => setSalaryModalTitle("Bulk Update")}
          >
            Bulk Update
          </div>

          <CustomModal
            modalTitle={salaryModalTitle}
            modalBody={
              salaryModalTitle === "Bulk Update" ? (
                <BulkUpdateModal />
              ) : (
                <SalaryMonthCardModal />
              )
            }
          />
          <CustomModal
            modalTitle={salaryModalTitle}
            modalId="terminatedEmployeeModal"
            modalBody={<TerminatedEmployeesModal />}
          />
          <CustomModal
            modalTitle="Wrong Punches"
            modalId="wrongPunchesModal"
            modalBody={<WrongPunchesModal employees={wrongPunchEmployees} />}
          />

          <div
            className="styled-btn--std me-2 mobile-d-none"
            onClick={() => {
              refetchPastMonth();
              attendanceToast();
            }}
          >
            Attendance <i className="bi bi-file-earmark-arrow-down" />
          </div>
          <HasRole roles={["admin", "super_admin"]}>
            <div
              className="styled-btn--std mobile-d-none"
              onClick={() => {
                updateClearLoans();
                clearLoanToast();
              }}
            >
              Clear Loans
            </div>
          </HasRole>
        </div>
      </div>
      <div className="overflow-auto salary-card-container">
        {isLoading && isLoadingHours && isLoadingSunday
          ? "Loading"
          : isSuccess && isSuccessHours && isSuccessSunday
          ? salary_details?.map((emp, key) => {
              if (
                emp?.emp_name
                  .toLowerCase()
                  .startsWith(employeeSearchBar.toLowerCase())
              ) {
                const empDetails = hours_past_month[emp.emp_id];
                return (
                  <SalaryCard
                    key={key}
                    emp_id={emp.emp_id}
                    emp_name={emp.emp_name}
                    loan={emp.loan}
                    rate={emp.rate}
                    last_increment={emp.last_increment}
                    travel={emp.travel}
                    working_hrs={empDetails?.working_hrs}
                    present={empDetails?.present}
                    eleven_hrs={empDetails?.eleven_hrs}
                    one_punch={empDetails?.one_punch}
                    extra={emp.extra}
                    loan_cut={emp.loan_cut}
                    add_hours={parseFloat(emp.add_hours)}
                    add_cash={emp.add_cash}
                    sundays={getSundayCount(emp.emp_id)}
                    emp_notes={emp.emp_notes}
                  />
                );
              }
            })
          : "Something went wrong"}
      </div>
    </div>
  );
};

export default Salary;
